import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { AdminProtectedRoute } from '@/lib/admin/admin-protected-route'
import { AdminLayout } from '@/components/layout/admin-layout'
import { useAdminAuth } from '@/lib/admin/admin-context'
import { useAuth } from '@/lib/auth/auth-context'
import { adminApi, type AdminUser } from '@/lib/api/admin'
import {
  Activity, Users, Search,
  CheckCircle, XCircle, Loader2, Play, UserCheck,
} from 'lucide-react'

type Tab = 'health' | 'users'

export default function AdminDashboardPage() {
  return (
    <AdminProtectedRoute>
      <AdminLayout>
        <DashboardContent />
      </AdminLayout>
    </AdminProtectedRoute>
  )
}

function DashboardContent() {
  const [tab, setTab] = useState<Tab>('health')

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'health', label: 'Health', icon: <Activity className="w-4 h-4" /> },
    { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
  ]

  return (
    <div>
      <h1 className="text-2xl font-bold text-zinc-900 mb-6">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-200/60 rounded-xl p-1 w-fit mb-8">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'health' && <HealthPanel />}
      {tab === 'users' && <UsersPanel />}
    </div>
  )
}

/* ─── Health Panel ───────────────────────────────────────── */

function HealthPanel() {
  const { adminKey } = useAdminAuth()
  const [backendStatus, setBackendStatus] = useState<'checking' | 'ok' | 'error'>('checking')
  const [workerJobId, setWorkerJobId] = useState<string | null>(null)
  const [workerLoading, setWorkerLoading] = useState(false)

  useEffect(() => {
    if (!adminKey) return
    adminApi.verifyKey(adminKey).then((ok) => setBackendStatus(ok ? 'ok' : 'error'))
  }, [adminKey])

  const triggerWorker = async () => {
    if (!adminKey) return
    setWorkerLoading(true)
    try {
      const { jobId } = await adminApi.triggerWorkerHealth(adminKey)
      setWorkerJobId(jobId)
    } catch {
      setWorkerJobId('error')
    } finally {
      setWorkerLoading(false)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Backend health */}
      <div className="bg-white rounded-xl border border-zinc-200 p-6">
        <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">Backend</h3>
        <div className="flex items-center gap-3">
          {backendStatus === 'checking' && <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />}
          {backendStatus === 'ok' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
          {backendStatus === 'error' && <XCircle className="w-5 h-5 text-red-500" />}
          <span className="text-lg font-medium text-zinc-900">
            {backendStatus === 'checking' ? 'Checking...' : backendStatus === 'ok' ? 'Healthy' : 'Unreachable'}
          </span>
        </div>
      </div>

      {/* Worker health */}
      <div className="bg-white rounded-xl border border-zinc-200 p-6">
        <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">Workers</h3>
        <button
          onClick={triggerWorker}
          disabled={workerLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors disabled:opacity-40"
        >
          {workerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Trigger Health Check
        </button>
        {workerJobId && (
          <p className="mt-3 text-sm text-zinc-500">
            Job ID: <code className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{workerJobId}</code>
          </p>
        )}
      </div>
    </div>
  )
}

/* ─── Users Panel ────────────────────────────────────────── */

const USERS_PAGE_SIZE = 50

function UsersPanel() {
  const { adminKey } = useAdminAuth()
  const { impersonateAs } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [impersonating, setImpersonating] = useState<number | null>(null)

  useEffect(() => {
    if (!adminKey) return
    setLoading(true)
    adminApi.listUsers(adminKey, { limit: USERS_PAGE_SIZE, offset })
      .then((page) => {
        setUsers(page.items)
        setTotal(page.total)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [adminKey, offset])

  const handleImpersonate = async (user: AdminUser) => {
    if (!adminKey) return
    setImpersonating(user.id)
    try {
      const { accessToken } = await adminApi.signInAs(adminKey, user.email)
      await impersonateAs(accessToken)
      navigate('/dashboard')
    } catch {
      setImpersonating(null)
    }
  }

  const filtered = users.filter((u) => {
    if (!search) return true
    const q = search.toLowerCase()
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().toLowerCase()
    return u.email.toLowerCase().includes(q) || name.includes(q)
  })

  const pageEnd = Math.min(offset + USERS_PAGE_SIZE, total)
  const hasPrev = offset > 0
  const hasNext = offset + USERS_PAGE_SIZE < total

  if (loading && users.length === 0) {
    return <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading users...</div>
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/50">
                <th className="text-left px-4 py-3 font-medium text-zinc-500">ID</th>
                <th className="text-left px-4 py-3 font-medium text-zinc-500">Name</th>
                <th className="text-left px-4 py-3 font-medium text-zinc-500">Email</th>
                <th className="text-left px-4 py-3 font-medium text-zinc-500">Admin</th>
                <th className="text-left px-4 py-3 font-medium text-zinc-500">Status</th>
                <th className="text-left px-4 py-3 font-medium text-zinc-500">Created</th>
                <th className="text-right px-4 py-3 font-medium text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors">
                  <td className="px-4 py-3 text-zinc-400 tabular-nums">{u.id}</td>
                  <td className="px-4 py-3 text-zinc-900 font-medium">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.admin ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md bg-amber-50 text-xs font-medium text-amber-700">Admin</span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.isActive ? 'text-emerald-600' : 'text-zinc-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 tabular-nums">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleImpersonate(u)}
                      disabled={impersonating === u.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors disabled:opacity-40"
                    >
                      {impersonating === u.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <UserCheck className="w-3 h-3" />}
                      Sign in as
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-400">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-400">
          <span>
            {total === 0 ? '0 users' : `${offset + 1}–${pageEnd} of ${total}`}
            {search && ` · ${filtered.length} matching`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - USERS_PAGE_SIZE))}
              disabled={!hasPrev || loading}
              className="px-2.5 py-1 rounded-md text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Prev
            </button>
            <button
              onClick={() => setOffset(offset + USERS_PAGE_SIZE)}
              disabled={!hasNext || loading}
              className="px-2.5 py-1 rounded-md text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

